import { RootState } from '..';
import rootGetters from '../getters';
import { getNestedField } from '../utils';
import { required } from '../validations';

export const getInvoice =
  (state: RootState) =>
    <T extends FlattenKeys<InvoiceState>>(field: T) =>
      getNestedField(state.invoice, field) as DeepType<InvoiceState, T>;

export const getDepositsFormMessage = (state: RootState) => () => {
  const {
    invoice: { getInvoice },
  } = rootGetters(state);

  const formData = getInvoice(`forms.deposits.createMany`);
  const blurData = getInvoice(`blur.deposits.createMany`);
  const messagesData = formData.map((data, index) => {
    const blur = blurData[index];
    const messages: Record<
      keyof InvoiceState['blur']['deposits']['createMany'][0],
      string
    > = {
      date: '',
      reference: '',
      value: '',
      company: '',
      locked: '',
      group: '',
    };

    messages.value =
      blur.value && ['', '0'].includes(`${data.value}`)
        ? 'Informe o valor do depósito'
        : '';

    messages.company =
      blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

    messages.date =
      blur.date && data.date === '' ? 'Informe a data do depósito' : '';

    messages.reference =
      blur.reference && data.reference === ''
        ? 'Informe a data de referência'
        : '';

    return messages;
  });

  return messagesData;
};

export const getFormMessage =
  (state: RootState) =>
    <F extends FlattenKeys<InvoiceState['blur'], 3, 1>>(
      form: F,
    ): Record<keyof DeepType<InvoiceState['blur'], F>, string> => {
      const {
        invoice: { getInvoice },
      } = rootGetters(state);

      if (form === 'invoices.create') {
        const data = getInvoice(`forms.invoices.create`);
        const blur = getInvoice(`blur.invoices.create`);
        const messages: Record<
          keyof InvoiceState['blur']['invoices']['create'],
          string
        > = {
          product: '',
          status: '',
          crt: '',
          invoiceNumber: '',
          date: '',
          invoiceValue: '',
          invoiceQuantity: '',
          unitCostReal: '',
          realValue: '',
          totalCost: '',
          unitCostTotal: '',
          unitSale: '',
          unitProfit: '',
          totalProfit: '',
          company: '',
          invoiceTaxes: '',
          observation: '',
          locked: '',
          labeled: '',
          group: '',
        };

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        messages.product =
          blur.product && data.product.resume === '' ? 'Informe o produto' : '';

        messages.date =
          blur.date && data.date === '' ? 'Informe a data da nota fiscal' : '';

        messages.crt = blur.crt && data.crt === '' ? 'Informe o CRT' : '';

        messages.status =
          blur.status &&
            !['RECEIVED', 'PENDING', 'CANCELED'].includes(data.status)
            ? 'Informe a situação'
            : '';

        messages.invoiceValue =
          blur.invoiceValue && ['', '0'].includes(`${data.invoiceValue}`)
            ? 'Informe o valor da nota fiscal'
            : '';

        messages.invoiceQuantity =
          blur.invoiceQuantity && ['', '0'].includes(`${data.invoiceQuantity}`)
            ? 'Informe a quantidade'
            : '';

        messages.invoiceNumber =
          blur.invoiceNumber && ['', '0'].includes(`${data.invoiceNumber}`)
            ? 'Informe o número da nota fiscal'
            : '';

        messages.unitCostReal =
          blur.unitCostReal && ['', '0'].includes(`${data.unitCostReal}`)
            ? 'Informe o valor unitário real'
            : '';

        messages.unitSale =
          blur.unitSale && ['', '0'].includes(`${data.unitSale}`)
            ? 'Informe o valor da venda unitária'
            : '';

        return messages as any;
      }
      if (form === 'invoices.update') {
        const data = getInvoice(`forms.invoices.update`);
        const blur = getInvoice(`blur.invoices.update`);
        const messages: Record<
          keyof InvoiceState['blur']['invoices']['update'],
          string
        > = {
          product: '',
          status: '',
          crt: '',
          invoiceNumber: '',
          date: '',
          invoiceValue: '',
          invoiceQuantity: '',
          unitCostReal: '',
          realValue: '',
          totalCost: '',
          unitCostTotal: '',
          unitSale: '',
          unitProfit: '',
          totalProfit: '',
          company: '',
          invoiceTaxes: '',
          observation: '',
          locked: '',
          labeled: '',
          group: '',
        };

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        messages.product =
          blur.product && data.product.resume === '' ? 'Informe o produto' : '';

        messages.status =
          blur.status &&
            !['RECEIVED', 'PENDING', 'CANCELED'].includes(data.status)
            ? 'Informe a situação'
            : '';

        messages.date =
          blur.date && data.date === '' ? 'Informe a data da nota fiscal' : '';

        messages.crt = blur.crt && data.crt === '' ? 'Informe o CRT' : '';

        messages.invoiceValue =
          blur.invoiceValue && ['', '0'].includes(`${data.invoiceValue}`)
            ? 'Informe o valor da nota fiscal'
            : '';

        messages.invoiceQuantity =
          blur.invoiceQuantity && ['', '0'].includes(`${data.invoiceQuantity}`)
            ? 'Informe a quantidade'
            : '';

        messages.invoiceNumber =
          blur.invoiceNumber && ['', '0'].includes(`${data.invoiceNumber}`)
            ? 'Informe o número da nota fiscal'
            : '';

        messages.unitCostReal =
          blur.unitCostReal && ['', '0'].includes(`${data.unitCostReal}`)
            ? 'Informe o valor unitário real'
            : '';

        messages.unitSale =
          blur.unitSale && ['', '0'].includes(`${data.unitSale}`)
            ? 'Informe o valor da venda unitária'
            : '';

        return messages as any;
      }
      if (form === 'taxes.create') {
        const data = getInvoice(`forms.taxes.create`);
        const blur = getInvoice(`blur.taxes.create`);
        const messages: Record<
          keyof InvoiceState['blur']['taxes']['create'],
          string
        > = {
          name: '',
          value: '',
          description: '',
        };
        messages.name = blur.name && !required(data.name) ? 'Informe o nome' : '';
        return messages as any;
      }
      if (form === 'taxes.update') {
        const data = getInvoice(`forms.taxes.update`);
        const blur = getInvoice(`blur.taxes.update`);
        const messages: Record<
          keyof InvoiceState['blur']['taxes']['update'],
          string
        > = {
          name: '',
          value: '',
          description: '',
        };
        messages.name = blur.name && !required(data.name) ? 'Informe o nome' : '';
        return messages as any;
      }
      if (form === 'companies.create') {
        const data = getInvoice(`forms.companies.create`);
        const blur = getInvoice(`blur.companies.create`);
        const messages: Record<
          keyof InvoiceState['blur']['companies']['create'],
          string
        > = {
          name: '',
        };
        messages.name = blur.name && !required(data.name) ? 'Informe o nome' : '';
        return messages as any;
      }
      if (form === 'companies.update') {
        const data = getInvoice(`forms.companies.update`);
        const blur = getInvoice(`blur.companies.update`);
        const messages: Record<
          keyof InvoiceState['blur']['companies']['update'],
          string
        > = {
          name: '',
        };
        messages.name = blur.name && !required(data.name) ? 'Informe o nome' : '';
        return messages as any;
      }
      if (form === 'deposits.create') {
        const data = getInvoice(`forms.deposits.create`);
        const blur = getInvoice(`blur.deposits.create`);
        const messages: Record<
          keyof InvoiceState['blur']['deposits']['create'],
          string
        > = {
          value: '',
          date: '',
          reference: '',
          company: '',
          locked: '',
          group: '',
        };

        messages.value =
          blur.value && ['', '0'].includes(`${data.value}`)
            ? 'Informe o valor do depósito'
            : '';

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        messages.date =
          blur.date && data.date === '' ? 'Informe a data do depósito' : '';

        messages.reference =
          blur.reference && data.reference === ''
            ? 'Informe a data de referência'
            : '';

        return messages as any;
      }
      if (form === 'deposits.update') {
        const data = getInvoice(`forms.deposits.update`);
        const blur = getInvoice(`blur.deposits.update`);
        const messages: Record<
          keyof InvoiceState['blur']['deposits']['update'],
          string
        > = {
          value: '',
          date: '',
          reference: '',
          company: '',
          locked: '',
          group: '',
        };

        messages.value =
          blur.value && ['', '0'].includes(`${data.value}`)
            ? 'Informe o valor do depósito'
            : '';

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        messages.date =
          blur.date && data.date === '' ? 'Informe a data do depósito' : '';

        messages.reference =
          blur.reference && data.reference === ''
            ? 'Informe a data de referência'
            : '';
        return messages as any;
      }
      if (form === 'deposits.updateMany') {
        const data = getInvoice(`forms.deposits.updateMany`);
        const blur = getInvoice(`blur.deposits.updateMany`);
        const messages: Record<
          keyof InvoiceState['blur']['deposits']['updateMany'],
          string
        > = {
          reference: '',
        };
        messages.reference =
          blur.reference && data.reference === ''
            ? 'Informe a data de referência'
            : '';
        return messages as any;
      }
      if (form === 'logistics.create') {
        const data = getInvoice(`forms.logistics.create`);
        const blur = getInvoice(`blur.logistics.create`);
        const messages: Record<
          keyof InvoiceState['blur']['logistics']['create'],
          string
        > = {
          quantityVolume: '',
          company: '',
          crt: '',
          clientName: '',
          nrcInvoiceNumber: '',
          clientInvoiceNumber: '',
          warehouseDispatchDate: '',
          revenueDate: '',
          customsDate: '',
          storageDate: '',
          clientDate: '',
          releaseDate: '',
          observation: '',
          group: '',
        };

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        return messages as any;
      }
      if (form === 'logistics.update') {
        const data = getInvoice(`forms.logistics.update`);
        const blur = getInvoice(`blur.logistics.update`);
        const messages: Record<
          keyof InvoiceState['blur']['logistics']['update'],
          string
        > = {
          quantityVolume: '',
          company: '',
          crt: '',
          clientName: '',
          nrcInvoiceNumber: '',
          clientInvoiceNumber: '',
          warehouseDispatchDate: '',
          revenueDate: '',
          customsDate: '',
          storageDate: '',
          clientDate: '',
          releaseDate: '',
          observation: '',
          group: '',
        };

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        return messages as any;
      }
      if (form === 'logisticOrigins.create') {
        const data = getInvoice(`forms.logisticOrigins.create`);
        const blur = getInvoice(`blur.logisticOrigins.create`);
        const messages: Record<
          keyof InvoiceState['blur']['logisticOrigins']['create'],
          string
        > = {
          originInvoiceNumber: '',
          quantityVolume: '',
          releaseDate: '',
          company: '',
          group: '',
        };

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        return messages as any;
      }
      if (form === 'logisticOrigins.update') {
        const data = getInvoice(`forms.logisticOrigins.update`);
        const blur = getInvoice(`blur.logisticOrigins.update`);
        const messages: Record<
          keyof InvoiceState['blur']['logisticOrigins']['update'],
          string
        > = {
          originInvoiceNumber: '',
          quantityVolume: '',
          releaseDate: '',
          company: '',
          group: '',
        };

        messages.company =
          blur.company && data.company.id === 0 ? 'Informe a companhia' : '';

        return messages as any;
      }
      return {} as any;
    };

const invoiceGetters = (state: RootState) => ({
  getInvoice: getInvoice(state),
  getFormMessage: getFormMessage(state),
  getDepositsFormMessage: getDepositsFormMessage(state),
});

export default invoiceGetters;
